import { Button, Spinner, Typography } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { NoricanLogo, NoricanMark } from "@/components/brand/norican-logo";
import { Microsoft } from "@/components/icons/microsoft";
import { logger } from "@/lib/logger";
import {
  inTeams,
  isEmbedded,
  mayBeTeams,
  signInSilentlyInTeams,
  signInViaPopup,
  signInViaTeams,
} from "@/lib/login-popup";

// Local dev can't complete a real Microsoft sign-in (Entra won't redirect back
// to localhost), so the dev server shows a login picker that hits the
// backend's `/auth/dev-login` instead — a genuine session, just without
// Microsoft. Production builds always show the Microsoft button; the backend
// refuses `/auth/dev-login` there regardless (see DEV_AUTH_BYPASS).
const SHOW_DEV_LOGIN = import.meta.env.DEV;

const SSO_LOGIN_URL = "/api/auth/sso/login";

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? "/home";
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  // Known before the click, so a plain popup outside Teams still opens within the click gesture.
  const [teams, setTeams] = useState<boolean | null>(null);
  // Inside Teams the page first signs in silently and only shows the button if that fails.
  const [silent, setSilent] = useState(mayBeTeams());

  useEffect(() => {
    if (!mayBeTeams()) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const host = await inTeams();
      if (cancelled) {
        return;
      }
      setTeams(host);
      if (!host) {
        setSilent(false);
        return;
      }
      try {
        await signInSilentlyInTeams();
        logger.debug("[login] signed in silently with Teams SSO");
        await queryClient.invalidateQueries({ queryKey: ["me"] });
        if (!cancelled) {
          navigate(returnTo, { replace: true });
        }
      } catch (err) {
        logger.debug("[login] Teams SSO not available, showing the sign-in button:", err);
        if (!cancelled) {
          setSilent(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient, returnTo]);

  const handleMicrosoftLogin = async () => {
    setError("");
    if (!isEmbedded()) {
      logger.debug("[login] redirecting to Microsoft SSO");
      window.location.assign(SSO_LOGIN_URL);
      return;
    }
    // Embedded: Entra will not load in a frame. Inside Teams the SDK opens
    // the sign-in window and hands the session back; elsewhere a popup does
    // and the session is picked up once /auth/me answers.
    setSigningIn(true);
    try {
      if (teams ?? (await inTeams())) {
        logger.debug("[login] inside Teams — signing in via the Teams SDK");
        await signInViaTeams(SSO_LOGIN_URL);
      } else {
        logger.debug("[login] embedded — signing in via popup");
        await signInViaPopup(SSO_LOGIN_URL);
      }
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(returnTo);
    } catch (err) {
      logger.debug("[login] popup sign-in did not complete:", err);
      setError(
        (err as Error).message === "popup_blocked"
          ? "Your browser blocked the sign-in window. Allow pop-ups for this app and try again."
          : "Sign-in was not completed. Please try again.",
      );
      setSigningIn(false);
    }
  };

  const handleDevLogin = async (role: "user" | "admin") => {
    logger.debug("[login] dev sign-in as", role);
    setError("");
    setSigningIn(true);
    try {
      const response = await fetch(`/api/auth/dev-login?role=${role}`, { method: "POST" });
      if (!response.ok) {
        throw new Error(`dev-login responded ${response.status}`);
      }
      logger.debug("[login] dev sign-in succeeded → redirecting to /home");
      navigate("/home");
    } catch (err) {
      logger.debug("[login] dev sign-in failed:", err);
      setError("Local sign-in failed. Please try again.");
      setSigningIn(false);
    }
  };

  useEffect(() => {
    const ssoError = searchParams.get("error");
    if (!ssoError) {
      return;
    }
    logger.debug("[login] sso error:", ssoError);
    setError(
      ssoError === "domain_not_allowed"
        ? "Your account isn't allowed to sign in to this app."
        : "Sign-in failed. Please try again.",
    );
  }, [searchParams]);

  if (silent) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="md" tone="neutral" />
      </div>
    );
  }

  return (
    <div className="bg-bg grid min-h-screen lg:grid-cols-2">
      {/* Brand panel — the Norican gradient from the guidelines, with the electrified line. */}
      <section className="relative flex flex-col justify-between overflow-hidden bg-[radial-gradient(130%_110%_at_10%_0%,var(--norican-color-brand-blue-accent)_0%,var(--norican-color-brand-blue-hover)_45%,var(--norican-color-brand-blue-700)_100%)] px-8 py-10 text-white sm:px-12 lg:py-14">
        <NoricanLogo variant="primary" className="h-14 w-auto" />
        <div className="relative py-16 lg:py-0">
          {/* The electrified line: a straight accent-blue line with a glow, never waved or recoloured. */}
          <div className="mb-10 h-0.5 w-full bg-[var(--norican-color-brand-blue-ring-dark)] shadow-[0_0_24px_6px_var(--norican-color-brand-blue-accent)]" />
          <Typography as="h1" variant="display-lg" tone="inverse" className="max-w-md text-white">
            Your business card. Always with you.
          </Typography>
          <Typography variant="body-lg" className="mt-4 max-w-md text-white/85">
            Generated from your Microsoft Entra profile, shared with one scan.
          </Typography>
        </div>
        <NoricanMark className="absolute right-8 bottom-8 size-8 text-white/90" />
      </section>

      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Typography as="h2" variant="heading-3">
            Sign in
          </Typography>
          <Typography variant="body-sm" tone="muted" className="mt-1 mb-6">
            Use your Norican work account.
          </Typography>
          {SHOW_DEV_LOGIN ? (
            <div className="flex flex-col gap-2">
              <Typography variant="caption" tone="muted" className="text-center">
                Local dev — Microsoft sign-in only works on the deployed app.
              </Typography>
              <Button
                type="button"
                disabled={signingIn}
                onClick={() => handleDevLogin("user")}
                fullWidth
              >
                Continue as User
              </Button>
              <Button
                type="button"
                disabled={signingIn}
                variant="outline"
                tone="neutral"
                onClick={() => handleDevLogin("admin")}
                fullWidth
              >
                Continue as Admin
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              disabled={signingIn}
              loading={signingIn}
              onClick={handleMicrosoftLogin}
              startIcon={<Microsoft className="size-4" />}
              fullWidth
            >
              Sign in with Microsoft
            </Button>
          )}
          {error && (
            <Typography variant="body-sm" tone="danger" className="mt-4 text-center">
              {error}
            </Typography>
          )}
        </div>
      </section>
    </div>
  );
}
